import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { MaskedLine, FadeUp } from "./Reveal";
import { useLightbox } from "./Lightbox";
import { IMAGES } from "@/data/content";

const ACRYLIC = [
  {
    src: "/images/acrylic-parallel-real.webp",
    alt: "Parallel kitchen in mint acrylic finish by STAR AVG ENTERPRISE",
    caption: "Parallel kitchen",
  },
  {
    src: "https://images.pexels.com/photos/7166563/pexels-photo-7166563.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
    alt: "L-shaped kitchen in beige acrylic finish",
    caption: "L-shaped kitchen",
  },
  {
    src: "/images/acrylic-ushaped-real.webp",
    alt: "U-shaped kitchen with dark counters and warm profile lighting by STAR AVG ENTERPRISE",
    caption: "U-shaped kitchen",
  },
  {
    src: "https://images.pexels.com/photos/7214158/pexels-photo-7214158.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
    alt: "Kitchen with breakfast counter in glossy acrylic finish",
    caption: "Breakfast counter",
  },
  {
    src: "https://images.pexels.com/photos/32331029/pexels-photo-32331029.png?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
    alt: "Slider wardrobe in high-gloss acrylic finish",
    caption: "Slider wardrobe",
  },
];

const CHAPTERS = [
  {
    n: "01",
    title: "Design before decoration",
    body: "Every project begins with how you live — not with a catalogue. We plan layouts, light and materials long before a single panel is built.",
  },
  {
    n: "02",
    title: "Made, not just drawn",
    body: "As furniture manufacturers, what we sketch is what we build. Modular kitchens, wardrobes and custom pieces — produced to exact measure.",
  },
  {
    n: "03",
    title: "One team to handover",
    body: "Design, construction, fitting and supply under one roof — so nothing is lost between the drawing and the finished room.",
  },
];

export default function Manifesto() {
  const imgRef = useRef(null);
  const { open } = useLightbox();
  const { scrollYProgress } = useScroll({ target: imgRef, offset: ["start end", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], [-50, 50]);

  return (
    <section id="studio" data-testid="studio-section" className="bg-sand py-12 lg:py-28">
      <div className="mx-auto max-w-[1400px] px-6 lg:px-12">
        <div className="mb-6 grid grid-cols-1 gap-8 lg:mb-16 lg:grid-cols-12">
          <div className="lg:col-span-7">
            <FadeUp>
              <p className="mb-10 flex items-center gap-3 text-[11px] font-semibold uppercase tracking-[0.35em] text-taupe">
                <span className="inline-block h-2 w-2 bg-terra" />
                The studio
              </p>
            </FadeUp>
            <h2 className="font-serif text-4xl leading-[1.08] tracking-tighter sm:text-5xl lg:text-6xl">
              <MaskedLine>A workshop mentality,</MaskedLine>
              <MaskedLine delay={0.12}>
                a <em className="text-terra">designer's eye.</em>
              </MaskedLine>
            </h2>
          </div>
          <div ref={imgRef} className="relative hidden overflow-hidden lg:col-span-4 lg:col-start-9 lg:block">
            <motion.img
              style={{ y }}
              src={IMAGES.wardrobe}
              alt="Contemporary wooden wardrobe crafted by STAR AVG ENTERPRISE"
              data-testid="studio-image"
              className="h-full w-full scale-110 object-cover"
            />
          </div>
        </div>
        <div className="space-y-0">
          {CHAPTERS.map((c, i) => (
            <FadeUp key={c.n} delay={i * 0.08}>
              <div
                data-testid={`chapter-${c.n}`}
                className="grid grid-cols-1 gap-6 border-t border-line py-7 last:border-b md:grid-cols-12 md:gap-12 lg:py-12"
              >
                <span className="font-serif text-5xl leading-none tracking-tighter text-terra md:col-span-3 md:text-6xl lg:text-8xl">
                  {c.n}
                </span>
                <div className="md:col-span-8 md:col-start-5">
                  <h3 className="mb-5 font-serif text-2xl tracking-tight sm:text-3xl">{c.title}</h3>
                  <p className="max-w-xl text-base leading-relaxed text-taupe">{c.body}</p>
                </div>
              </div>
            </FadeUp>
          ))}
        </div>
        <div data-testid="acrylic-collection" className="mt-12 lg:mt-16">
          <FadeUp>
            <p className="mb-2 flex items-center gap-3 text-[11px] font-semibold uppercase tracking-[0.35em] text-taupe">
              <span className="inline-block h-2 w-2 bg-terra" />
              Acrylic finish — layouts we build
            </p>
            <p className="mb-8 text-sm text-taupe">High-gloss acrylic shutters, five ways. Tap any look to view it large.</p>
          </FadeUp>
          <div className="grid grid-cols-2 gap-4 md:grid-cols-3 lg:grid-cols-5">
            {ACRYLIC.map((a, i) => (
              <FadeUp key={a.caption} delay={i * 0.06} className="h-full">
                <figure
                  data-testid={`acrylic-${i + 1}`}
                  onClick={() => open(ACRYLIC, i)}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => e.key === "Enter" && open(ACRYLIC, i)}
                  className="group h-full cursor-zoom-in"
                >
                  <div className="overflow-hidden">
                    <img
                      src={a.src}
                      alt={a.alt}
                      loading="lazy"
                      className="aspect-[3/4] w-full object-cover transition-transform duration-700 group-hover:scale-105"
                    />
                  </div>
                  <figcaption className="mt-3 flex items-center gap-2 text-[10px] uppercase tracking-[0.25em] text-taupe">
                    <span className="inline-block h-1.5 w-1.5 bg-terra" />
                    {a.caption}
                  </figcaption>
                </figure>
              </FadeUp>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
